import { Alert, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { MedicationForm } from "src/api/types";
import { usePrescribe } from "src/context/PrescribeProvider/usePrescribe";
import { usePrescription } from "src/context/PrescriptionProvider";
import { useTitration } from "src/context/TitrationProvider/useTitration";
import { isTruthy } from "src/utils/isTruthy";

const mapMedicationFormToDosageLeft: Record<
  MedicationForm,
  (totalDose: number, strength: number) => number
> = {
  injectable: (totalDose) => totalDose * 4,
  oral: (totalDose, strength) => totalDose * 30 * strength,
  dropper: () => 0,
  topical: () => 0,
};

const TotalDose = () => {
  const currentTier = useTitration();
  const quantity = usePrescription((p) => p.dispense_quantity);
  const daysSupply = usePrescription(
    (prescription) => prescription.days_supply,
  );
  const requestedDosage = usePrescription(
    (prescription) => (prescription.metadata as Record<string, string>)?.dosage,
  );

  const { medications, selectedMedications, dosages, medication } =
    usePrescribe();

  const [total, unit] = useMemo(() => {
    const unit = medication?.dosage.split(" ")[1] ?? null;

    const total = selectedMedications.filter(isTruthy).reduce((acc, id) => {
      const medication = medications.find((m) => m.id === id);

      if (medication != null) {
        return acc + parseFloat(medication.dosage);
      }

      return acc;
    }, 0);

    return [total, unit];
  }, [medication, medications, selectedMedications]);

  const [totalDosageLeft, dosageUnit] = useMemo(() => {
    if (medication == null) return [];
    const selectedDosages = dosages.filter(isTruthy);
    const strength = medication?.medication_strength[0];

    if (selectedDosages.length === 0) return [];

    const totalDosages = selectedDosages.reduce((acc, dosage) => {
      return acc + parseFloat(dosage);
    }, 0);

    const totalDosageLeft = mapMedicationFormToDosageLeft[medication.form](
      totalDosages,
      strength?.strength_value ?? 1,
    );

    return [totalDosageLeft, strength.strength_unit];
  }, [dosages, medication]);

  const totalAllowed = useMemo(() => {
    if (medication?.form === "injectable") {
      return total;
    }

    if (currentTier != null) {
      return currentTier.total_dose;
    }

    return null;
  }, [currentTier, medication?.form, total]);

  const medicationInterval = useMemo(() => {
    if (daysSupply == null) return "";

    return daysSupply === 84 ? ", Quarterly supply" : ", Monthly supply";
  }, [daysSupply]);

  const alert = useMemo(() => {
    if (quantity != null) {
      return (
        <Alert severity="info">{`Patient request ${quantity} pills`}</Alert>
      );
    }

    if (requestedDosage != null) {
      return (
        <Alert severity="info">
          <Typography>{`Patient has requested a max dose of ${requestedDosage}${medicationInterval}`}</Typography>
        </Alert>
      );
    }

    if (currentTier != null) {
      return (
        <Alert severity="info">
          <Typography>{`Patient NEXT MAX dose is ${currentTier.max_dose} ${currentTier.dose_unit}${medicationInterval}`}</Typography>
        </Alert>
      );
    }

    return null;
  }, [currentTier, medicationInterval, quantity, requestedDosage]);

  return (
    <Stack gap={1}>
      {alert}
      <Stack direction="row" gap={1}>
        <Typography fontWeight={600}>Selected medication: </Typography>
        {medication != null ? (
          <>
            <Typography>{`${medication?.display_name} ${
              medication.dosage === "N/A" ? "" : `- ${total} ${unit}`
            }`}</Typography>
            {currentTier && medication.form === "injectable" && (
              <Typography
                color={total !== currentTier.total_dose ? "error" : "success"}
              >{`(${currentTier.total_dose} ${currentTier.dose_unit})`}</Typography>
            )}
          </>
        ) : null}
      </Stack>
      {totalAllowed != null && totalDosageLeft != null ? (
        <Stack direction="row" gap={1}>
          <Typography fontWeight={600}>Total dosage: </Typography>
          <Typography
            color={totalDosageLeft <= totalAllowed ? "success" : "error"}
          >{`(${totalDosageLeft} / ${totalAllowed} ${dosageUnit})`}</Typography>
        </Stack>
      ) : null}
    </Stack>
  );
};

export default TotalDose;
